import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  ModuleBreadcrumbs,
  ScrollArea,
  Skeleton,
  useGql,
} from '@ah/design';
import { GATHERINGS_QUERY, type GatheringRow } from '../shared/index.ts';
import { GatheringsLayout } from './GatheringsLayout.tsx';
import { GatheringCard } from './GatheringCard.tsx';

function GatheringListSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardHeader className="pb-2"><Skeleton className="h-4 w-20 mb-2" /><Skeleton className="h-5 w-3/4" /></CardHeader>
          <CardContent><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-1/2" /></CardContent>
        </Card>
      ))}
    </div>
  );
}

export function AttendingGatherings() {
  const gql = useGql();
  const navigate = useNavigate();

  const { data: gatheringsList, isLoading, error } = useQuery({
    queryKey: ['gatherings'],
    queryFn: async () => (await gql.request<{ gatherings: GatheringRow[] }>(GATHERINGS_QUERY)).gatherings,
  });

  const now = Date.now();
  const attending = (gatheringsList ?? [])
    .filter((g) =>
      g.currentUserParticipant?.status === 'reserved'
      && new Date(g.endsAt).getTime() >= now,
    )
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());

  return (
    <GatheringsLayout>
      <ModuleBreadcrumbs segments={[{ label: 'Gatherings', to: '/gatherings' }, { label: 'Attending' }]} />
      <div className="max-w-5xl mx-auto px-6 py-8">
        {error && (
          <Card><CardContent className="pt-6">
            <p className="text-sm text-destructive text-center">{(error as Error).message}</p>
          </CardContent></Card>
        )}

        {isLoading ? <GatheringListSkeleton /> : attending.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-3">
            <CalendarCheck className="w-12 h-12 text-muted-foreground" />
            <div className="text-center space-y-1">
              <p className="font-medium text-foreground">You're not signed up for any gatherings yet</p>
              <p className="text-sm text-muted-foreground">RSVP to a gathering and it'll appear here.</p>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-180px)]">
            <div className="space-y-3 pr-4">
              {attending.map((g) => <GatheringCard key={g.id} gathering={g} onClick={() => navigate(`/gatherings/${g.id}`)} />)}
            </div>
          </ScrollArea>
        )}
      </div>
    </GatheringsLayout>
  );
}
